/**
 * The committed value, struck through, under the control replacing it.
 *
 * The sibling half of the staged treatment. `StagedControl` wraps a control to
 * do both marks at once; where the control already sits in a container that can
 * take `stagedControlSx` itself, this renders the value alone and nothing gets
 * re-indented — which matters because a re-indented line counts as a new line
 * to the coverage gate even though istanbul records no statement on it.
 */
import React from 'react';
import { Typography } from '@mui/material';
import { useDraft } from '../DraftContext';
import type { FieldPath } from '../types';
import { stagedWasSx } from './stagedStyles';

export interface StagedWasProps {
  readonly paths: readonly FieldPath[];
  /** `was $3,000,000` rather than a bare figure — see `StagedControl`. */
  readonly prefixed?: boolean;
}

const StagedWas = ({ paths, prefixed }: StagedWasProps) => {
  const { stagedFields } = useDraft();
  const changes = stagedFields.at(paths);

  if (changes.length === 0) {
    return null;
  }

  return (
    <>
      {changes.map((change) => (
        <Typography
          key={change.id}
          variant="caption"
          color="text.disabled"
          sx={stagedWasSx}
        >
          {prefixed ? 'was ' : ''}
          {change.fromLabel}
        </Typography>
      ))}
    </>
  );
};

export default StagedWas;
